/**
 * HeuristicPopup - Code Auto-Correction Dialog
 * 
 * Popup displayed when bad code is detected in AI responses.
 * Offers: Repair (with LLM + Skills context), Skip, or Execute as-is.
 * 
 * Part of the ReAct Mode: Agent "knows where to knock" - uses Skills positive
 * context as guiding signal for auto-correction.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { authHeaders } from '../utils/auth';

export interface HeuristicWarning {
  message: string;
  severity: number;
  line: number;
}

export interface SkillsContext {
  skill: string;
  fix_suggestion: string;
  positive_pattern: string;
  warning_message: string;
}

export interface CodeBlock {
  block_index: number;
  code: string;
  analysis: {
    risk_score: number;
    overall_status: string;
    warnings: HeuristicWarning[];
    syntax_valid: boolean;
  };
  skills_context: SkillsContext[];
  needs_repair: boolean;
}

export interface HeuristicPopupProps {
  blocks: CodeBlock[];
  onRepair: (originalCode: string, repairedCode: string, blockIndex: number) => void;
  onSkip: () => void;
  onExecuteOriginal?: (code: string) => void;
  visible: boolean;
}

const statusColors: Record<string, { bg: string; text: string; glow: string }> = {
  SAFE:     { bg: 'rgba(16,185,129,0.1)', text: '#10b981', glow: '0 0 12px rgba(16,185,129,0.4)' },
  WARNINGS: { bg: 'rgba(245,158,11,0.1)', text: '#f59e0b', glow: '0 0 12px rgba(245,158,11,0.4)' },
  RISKY:    { bg: 'rgba(249,115,22,0.1)', text: '#f97316', glow: '0 0 12px rgba(249,115,22,0.4)' },
  CRITICAL: { bg: 'rgba(239,68,68,0.1)',  text: '#ef4444', glow: '0 0 12px rgba(239,68,68,0.4)' },
};

const severityIcon = (s: number) => {
  if (s >= 10) return '🔴';
  if (s >= 7)  return '🟠';
  if (s >= 5)  return '🟡';
  return '🟢';
};

export const HeuristicPopup: React.FC<HeuristicPopupProps> = ({
  blocks,
  onRepair,
  onSkip,
  onExecuteOriginal,
  visible
}) => {
  const [activeBlockIdx, setActiveBlockIdx] = useState(0);
  const [repairing, setRepairing] = useState(false);
  const [repairedCode, setRepairedCode] = useState<string | null>(null);
  const [repairResult, setRepairResult] = useState<any | null>(null);
  const [repairError, setRepairError] = useState<string | null>(null);
  const [showRepaired, setShowRepaired] = useState(false);

  const activeBlock = blocks[activeBlockIdx];

  // Reset state when new blocks arrive
  useEffect(() => {
    if (visible) {
      setActiveBlockIdx(0);
      setRepairedCode(null);
      setRepairResult(null);
      setRepairError(null);
      setShowRepaired(false);
      setRepairing(false);
    }
  }, [visible, blocks]);

  const handleRepair = useCallback(async () => {
    if (!activeBlock) return;
    setRepairing(true);
    setRepairError(null);
    setRepairedCode(null);
    setRepairResult(null);

    try {
      const requestRepair = async (useLlm: boolean) => {
        const response = await fetch('/v1/heuristics/repair', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            code: activeBlock.code,
            warnings: activeBlock.analysis.warnings,
            use_llm: useLlm
          })
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'unknown error');
          throw new Error(`Repair endpoint returned ${response.status}: ${errorText}`);
        }

        return response.json();
      };

      let data: any;
      let usedFallback = false;

      try {
        // Prefer full LLM+skills repair path
        data = await requestRepair(true);
      } catch (primaryError) {
        // Frontend fallback: when LLM path fails (e.g. backend 500), try deterministic repair
        usedFallback = true;
        data = await requestRepair(false);
      }

      if (!data?.repaired_code) {
        throw new Error('Repair response missing "repaired_code"');
      }

      setRepairedCode(data.repaired_code);
      setRepairResult({
        ...data,
        model_used: data.model_used || (usedFallback ? 'fallback-rule-engine' : 'llm'),
        fallback_used: usedFallback,
      });
      setShowRepaired(true);
    } catch (e: any) {
      setRepairError(`Naprawa nieudana: ${e.message}`);
    } finally {
      setRepairing(false);
    }
  }, [activeBlock]);

  const handleAcceptRepair = () => {
    if (!repairedCode || !activeBlock) return;
    onRepair(activeBlock.code, repairedCode, activeBlockIdx);
  };

  if (!visible || !activeBlock) return null;

  const { bg, text, glow } = statusColors[activeBlock.analysis.overall_status] || statusColors.WARNINGS;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(6px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      animation: 'fadeIn 0.2s ease'
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes pulse { 0%,100% { box-shadow: ${glow}; } 50% { box-shadow: none; } }
        .heuristic-popup-code {
          font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
          font-size: 11px;
          background: rgba(0,0,0,0.4);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 8px;
          padding: 12px;
          overflow-x: auto;
          max-height: 200px;
          overflow-y: auto;
          white-space: pre;
          color: #e2e8f0;
          line-height: 1.5;
        }
        .skill-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          background: rgba(168,85,247,0.15);
          border: 1px solid rgba(168,85,247,0.3);
          border-radius: 20px;
          font-size: 10px;
          color: #c084fc;
          font-weight: 600;
        }
      `}</style>

      <div style={{
        background: 'linear-gradient(135deg, #0f1117 0%, #1a1d28 100%)',
        border: `1px solid ${text}44`,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '680px',
        maxHeight: '92vh',
        overflow: 'auto',
        boxShadow: `0 24px 48px rgba(0,0,0,0.6), ${glow}`,
        animation: 'pulse 2s ease infinite'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px 12px',
          borderBottom: `1px solid ${text}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: bg,
          borderRadius: '16px 16px 0 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ fontSize: '22px' }}>
              {activeBlock.analysis.overall_status === 'CRITICAL' ? '🚨' :
               activeBlock.analysis.overall_status === 'RISKY' ? '⚠️' : '🔍'}
            </div>
            <div>
              <div style={{ color: text, fontWeight: 700, fontSize: '15px', letterSpacing: '-0.3px' }}>
                Heuristic Engine — Wykryto Problemy w Kodzie
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginTop: '2px' }}>
                Status: <span style={{ color: text, fontWeight: 600 }}>{activeBlock.analysis.overall_status}</span>
                {' · '}Risk Score: <span style={{ color: text }}>{activeBlock.analysis.risk_score}</span>
                {blocks.length > 1 && ` · Blok ${activeBlockIdx + 1}/${blocks.length}`}
              </div>
            </div>
          </div>
          <button
            onClick={onSkip}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.3)', fontSize: '20px', lineHeight: 1,
              padding: '4px', borderRadius: '6px',
              transition: 'color 0.2s'
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.8)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          {/* Block tabs (if multiple blocks) */}
          {blocks.length > 1 && (
            <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {blocks.map((b, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveBlockIdx(i); setShowRepaired(false); setRepairedCode(null); }}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '20px',
                    border: `1px solid ${i === activeBlockIdx ? text : 'rgba(255,255,255,0.1)'}`,
                    background: i === activeBlockIdx ? `${text}22` : 'transparent',
                    color: i === activeBlockIdx ? text : 'rgba(255,255,255,0.5)',
                    fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {b.needs_repair ? '⚠️' : '✅'} Blok {i + 1}
                </button>
              ))}
            </div>
          )}

          {/* Warnings list */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
              Wykryte Problemy
            </div>
            {activeBlock.analysis.warnings.map((w, i) => (
              <div key={i} style={{
                display: 'flex', gap: '10px', alignItems: 'flex-start',
                padding: '8px 12px', marginBottom: '6px',
                background: `rgba(239,68,68,${w.severity >= 8 ? 0.1 : 0.05})`,
                border: `1px solid rgba(239,68,68,${w.severity >= 8 ? 0.25 : 0.1})`,
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '14px', flexShrink: 0 }}>{severityIcon(w.severity)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#e2e8f0', fontSize: '12px', lineHeight: '1.4' }}>{w.message}</div>
                  {w.line > 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px', marginTop: '3px' }}>Linia {w.line} · Severity: {w.severity}/10</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Skills Positive Context */}
          {activeBlock.skills_context && activeBlock.skills_context.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
                🎯 Skills Positive Context — Wzorce naprawy
              </div>
              {activeBlock.skills_context.map((ctx, i) => (
                <div key={i} style={{
                  padding: '10px 12px', marginBottom: '8px',
                  background: 'rgba(168,85,247,0.08)',
                  border: '1px solid rgba(168,85,247,0.2)',
                  borderRadius: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <span className="skill-badge">📚 {ctx.skill}</span>
                  </div>
                  <div style={{ color: '#a78bfa', fontSize: '11px', marginBottom: '5px' }}>
                    ✅ <strong>Pozytywny wzorzec:</strong> {ctx.positive_pattern}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>
                    🔧 <strong>Sugerowana naprawa:</strong> {ctx.fix_suggestion}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Original Code Preview */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
              Oryginalny Kod
            </div>
            <div className="heuristic-popup-code">{activeBlock.code}</div>
          </div>

          {/* Repaired Code (shown after repair) */}
          {showRepaired && repairedCode && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ color: '#10b981', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  ✅ Naprawiony Kod
                </div>
                {repairResult && (
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {repairResult.skills_applied?.map((s: string) => (
                      <span key={s} className="skill-badge">📚 {s}</span>
                    ))}
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>
                      via {repairResult.model_used}
                    </span>
                  </div>
                )}
              </div>
              <div className="heuristic-popup-code" style={{ border: '1px solid rgba(16,185,129,0.3)' }}>
                {repairedCode}
              </div>
              {repairResult?.repaired_risk_score === 0 && (
                <div style={{ marginTop: '8px', color: '#10b981', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>✅</span> Kod bezpieczny po naprawie · Risk Score: 0
                </div>
              )}
              {repairResult?.repaired_risk_score > 0 && (
                <div style={{ marginTop: '8px', color: '#f59e0b', fontSize: '11px' }}>
                  ⚠️ Risk Score po naprawie: {repairResult.repaired_risk_score} — sprawdź manualnie
                </div>
              )}
            </div>
          )}

          {repairError && (
            <div style={{ marginBottom: '14px', padding: '10px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px', color: '#fca5a5', fontSize: '12px' }}>
              ❌ {repairError}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {/* Repair Button */}
            {!showRepaired ? (
              <button
                onClick={handleRepair}
                disabled={repairing}
                id="heuristic-repair-btn"
                style={{
                  flex: '1', minWidth: '160px',
                  padding: '11px 20px',
                  background: repairing 
                    ? 'rgba(168,85,247,0.3)' 
                    : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  border: 'none', borderRadius: '10px',
                  color: '#fff', fontWeight: 700, fontSize: '13px',
                  cursor: repairing ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: repairing ? 'none' : '0 4px 15px rgba(168,85,247,0.4)'
                }}
              >
                {repairing ? (
                  <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⚙️</span> Naprawiam z LLM + Skills...</>
                ) : (
                  <><span>🔧</span> Napraw z Skills Context</>
                )}
              </button>
            ) : (
              <button
                onClick={handleAcceptRepair}
                id="heuristic-accept-repair-btn"
                style={{
                  flex: '1', minWidth: '160px',
                  padding: '11px 20px',
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  border: 'none', borderRadius: '10px',
                  color: '#fff', fontWeight: 700, fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: '0 4px 15px rgba(16,185,129,0.4)'
                }}
              >
                <span>✅</span> Akceptuj i wstaw naprawiony
              </button>
            )}

            {/* Execute Original */}
            {onExecuteOriginal && (
              <button
                onClick={() => onExecuteOriginal(activeBlock.code)}
                id="heuristic-execute-original-btn"
                style={{
                  padding: '11px 16px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
              >
                <span>▶</span> Uruchom oryginał
              </button>
            )}

            {/* Retry Repair */}
            {showRepaired && (
              <button
                onClick={() => { setShowRepaired(false); setRepairedCode(null); }}
                style={{
                  padding: '11px 16px',
                  background: 'rgba(168,85,247,0.08)',
                  border: '1px solid rgba(168,85,247,0.2)',
                  borderRadius: '10px',
                  color: '#a78bfa', fontWeight: 600, fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                ↩ Napraw ponownie
              </button>
            )}

            {/* Skip */}
            <button
              onClick={onSkip}
              id="heuristic-skip-btn"
              style={{
                padding: '11px 16px',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: '10px',
                color: '#fca5a5', fontWeight: 600, fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.08)')}
            >
              ✕ Pomiń
            </button>
          </div>

          {/* Mode hint */}
          <div style={{
            marginTop: '14px', paddingTop: '12px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.3)', fontSize: '10px',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <span>⚡</span>
            <span>ReAct Auto-Correction Mode aktywny · Heuristic Engine wersja 2.0 · Skills Positive Context</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeuristicPopup;
